/**
 * Consolidated formatter and text generation utilities for Driver Debug view.
 * Keeps all formatting logic in one place instead of spreading across multiple files.
 */
import { ApiAnnotationDriverDay, ApiDuty } from '@/rennovation/planning-view/types/api/roster-response';
import { useI18n } from 'vue-i18n';
import { safeFormatSecondsAsTime, weekdayShort, formatDayDate } from '@/rennovation/shared/utils/date-utils';

export function useDriverDebugFormatters() {
	const { t } = useI18n();

	function shiftTimeText(day: ApiAnnotationDriverDay): string {
		const d = day.duty;
		return d && d.start !== undefined && d.end !== undefined ? `${safeFormatSecondsAsTime(d.start)} – ${safeFormatSecondsAsTime(d.end)}` : '';
	}

	function standbyTimeText(day: ApiAnnotationDriverDay): string {
		const d = day.duty;
		return d && d.standbyStart != null && d.standbyEnd != null
			? `${safeFormatSecondsAsTime(d.standbyStart)} – ${safeFormatSecondsAsTime(d.standbyEnd)}`
			: '';
	}

	function workTimeText(day: ApiAnnotationDriverDay): string {
		const stats = day.statistics;
		const actual = day.duty && stats?.workTime !== undefined ? safeFormatSecondsAsTime(stats.workTime) + ' ' : '';
		const target = stats?.targetWorkTime !== undefined ? `(${safeFormatSecondsAsTime(stats.targetWorkTime)})` : '';
		return actual + target;
	}

	function overtimeText(day: ApiAnnotationDriverDay): string {
		const stats = day.statistics;
		if (stats?.currentOvertime === undefined || stats?.overtimeBalance === undefined) return '';
		const compensation = (stats.timeAllowanceCompensation ?? 0) + (stats.extraWorkCompensation ?? 0);
		return `${safeFormatSecondsAsTime(stats.overtimeBalance)} (${safeFormatSecondsAsTime(stats.currentOvertime + compensation)})`;
	}

	function restDayBalanceText(day: ApiAnnotationDriverDay): string {
		const stats = day.statistics;
		if (stats?.restDayBalance === undefined || stats?.currentRestDaysDifference === undefined) return '';
		return `${Math.round(stats.restDayBalance * 100) / 100} (${Math.round(stats.currentRestDaysDifference * 100) / 100})`;
	}

	function vacationText(day: ApiAnnotationDriverDay): string {
		const s = day.statistics;
		if (
			s?.totalAvailableVacation === undefined ||
			s?.totalUsedVacation === undefined ||
			s?.yearlyUsedVacation === undefined ||
			s?.vacationBalance === undefined
		)
			return '';
		return `${s.totalUsedVacation.toFixed(2)}d (${s.yearlyUsedVacation.toFixed(2)}d) / ${s.totalAvailableVacation.toFixed(2)}d (${s.vacationBalance.toFixed(2)}d)`;
	}

	function dutyTooltip(duty: ApiDuty | null): string {
		if (!duty?.type) return '';
		if (duty.isRestSunday) return t('duty_dayType_restSunday');
		return t(`duty_dayType_${duty.type}`);
	}

	return {
		shiftTimeText,
		standbyTimeText,
		workTimeText,
		overtimeText,
		restDayBalanceText,
		vacationText,
		dutyTooltip,
	};
}
